import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import './index.less';

import IbSpin from '../../components/common/IbSpin';
import { getQueryVariable } from '../../utils/queryUtil';
import { passwordApi, trialApi } from '../../apis';
import IbIcon from '../../components/common/IbIcon';
import { PasswordOptionsResponse, TrialAcceptionRequest } from '../../../api';
import { getErrorMessage } from '../../utils/exceptionUtil';
import IbInfo from '../../components/common/IbInfo';
import IbInput from '../../components/common/IbInput';
import IbButton from '../../components/common/IbButton';
import IbTypography from '../../components/common/IbTypography';

const MAIN_CLASS = 'ib-activation-page';
const LOADER_CLASS = `${MAIN_CLASS}__loader`;
const PASSWORD_CLASS = `${MAIN_CLASS}__password`;
const REDIRECT_TIMEOUT = 3000;

const ConfirmTrialPage: React.FC = () => {
  const { push } = useHistory();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activated, setActivated] = useState(false);
  const [userId, setUserId] = useState('');
  const [trialId, setTrialId] = useState('');
  const [code, setCode] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordOptionsResponse>();

  const activateTrialAsync = async () => {
    const verificationToken = getQueryVariable('verificationToken') ?? '';
    try {
      const link = await trialApi.confirmTrial(verificationToken);
      setUserId(link.data.userId);
      setTrialId(link.data.trialId);
      setCode(link.data.resetPasswordToken);
      setActivated(true);
    } catch (e) {
      if (e.response.status == 404) {
        setErrorMessage(t('This account has already been activated.'));
        return;
      }
      setErrorMessage(t('Server error when trying to activate account. Please wait and try again.'));
    }
  };

  const activateTrial = () => {
    activateTrialAsync().finally();
  };
  useEffect(activateTrial, []);

  const renderDefault = () => (
    <div className={LOADER_CLASS}>
      <IbSpin />
      <div className={`${LOADER_CLASS}__title`}>{t('Activation in progress')}</div>
      <div className={`${LOADER_CLASS}__description`}>{t('Please wait')}</div>
    </div>
  );

  const renderError = () => (
    <div className={LOADER_CLASS}>
      <IbIcon iconName="attention" size={64} />
      <div className={`${LOADER_CLASS}__title`}>{t('Failed to activate')}</div>
      <div className={`${LOADER_CLASS}__description`}>{errorMessage}</div>
    </div>
  );

  const loadPasswordRequirementsAsync = async () => {
    try {
      const passwordRequirements = await passwordApi.getPasswordOptions();
      setPasswordRequirements(passwordRequirements.data);
    } catch (e) {
      setPasswordRequirements(undefined);
    }
  };

  const loadPasswordRequirements = () => {
    loadPasswordRequirementsAsync().finally();
  };
  useEffect(loadPasswordRequirements, []);

  const getPasswordRequirementsDescription = (validate = false) => {
    if (!passwordRequirements) return;

    // eslint-disable-next-line security/detect-non-literal-regexp
    const lengthRequirementRegexp = new RegExp(`.{${passwordRequirements.requiredLength},}`);
    const lowerCaseRequirement = /[a-z]/;
    const upperCaseRequirement = /[A-Z]/;
    const digitRequirement = /[0-9]/;
    const specialCharacterRequirement = /[^a-zA-Z0-9_\s]/;

    const startDescription = t('Password must contain');
    const lengthRequirement =
      !validate || !lengthRequirementRegexp.test(password)
        ? ` ${t('at least')} ${passwordRequirements.requiredLength} ${t('characters')}`
        : '';

    let lettersRequirement = lengthRequirement ? `, ${t('including')}` : '';
    if (
      passwordRequirements.requireLowercase &&
      passwordRequirements.requireUppercase &&
      (!validate || (!lowerCaseRequirement.test(password) && !upperCaseRequirement.test(password)))
    ) {
      lettersRequirement = lettersRequirement + ' ' + t('lowercase and uppercase Latin letters');
    } else if (passwordRequirements.requireLowercase && (!validate || !lowerCaseRequirement.test(password))) {
      lettersRequirement = lettersRequirement + ' ' + t('lowercase Latin letters');
    } else if (passwordRequirements.requireUppercase && (!validate || !upperCaseRequirement.test(password))) {
      lettersRequirement = lettersRequirement + ' ' + t('uppercase Latin letters');
    } else {
      lettersRequirement = '';
    }

    let nonLettersRequirement = lengthRequirement || lettersRequirement ? `, ${t('as well as')}` : '';
    if (
      passwordRequirements.requireDigit &&
      passwordRequirements.requireNonAlphanumeric &&
      (!validate || (!digitRequirement.test(password) && !specialCharacterRequirement.test(password)))
    ) {
      nonLettersRequirement = nonLettersRequirement + ' ' + t('at least one number and one special character');
    } else if (passwordRequirements.requireDigit && (!validate || !digitRequirement.test(password))) {
      nonLettersRequirement = nonLettersRequirement + ' ' + t('at least one number');
    } else if (
      passwordRequirements.requireNonAlphanumeric &&
      (!validate || !specialCharacterRequirement.test(password))
    ) {
      nonLettersRequirement = nonLettersRequirement + ' ' + t('at least one special character');
    } else {
      nonLettersRequirement = '';
    }

    if (lengthRequirement || lettersRequirement || nonLettersRequirement) {
      return startDescription + lengthRequirement + lettersRequirement + nonLettersRequirement;
    }
  };

  useEffect(() => {
    if (!successMessage) return;
    setTimeout(() => {
      push('/app/login');
    }, REDIRECT_TIMEOUT);
  }, [successMessage]);

  const handleSubmit = async () => {
    const isValid = password === confirmPassword;
    if (!isValid) {
      setErrorMessage(t('The entered passwords do not match'));
      return;
    }

    const validationError = getPasswordRequirementsDescription(true);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setErrorMessage('');
    setLoading(true);

    const options = { withCredentials: true };
    const request: TrialAcceptionRequest = {
      password,
      code,
    };

    try {
      await trialApi.activateTrial(trialId, userId, request, options);
      setSuccessMessage(t('The password was successfully saved. You will be redirected to the login page.'));
    } catch (e) {
      setErrorMessage(getErrorMessage(e as Error));
    }
    setLoading(false);
  };

  const onPasswordChange = (value: string) => {
    setErrorMessage('');
    setPassword(value);
  };
  const onConfirmPasswordChange = (value: string) => {
    setErrorMessage('');
    setConfirmPassword(value);
  };
  const onShowPasswordClick = () => setShowPassword(!showPassword);
  const onShowConfirmPasswordClick = () => setShowConfirmPassword(!showConfirmPassword);
  const renderTogglePasswordIcon = (show: boolean) =>
    show ? <IbIcon iconName="preview-open" /> : <IbIcon iconName="preview-close-one" />;

  const renderSetPasswordForm = () => (
    <div className={PASSWORD_CLASS}>
      {loading && <IbSpin />}
      <div className={`${PASSWORD_CLASS}__title`}>{t('Create a password')}</div>
      {successMessage && <IbInfo status="success">{successMessage}</IbInfo>}
      {passwordRequirements && !successMessage && (
        <IbInfo status="error">{getPasswordRequirementsDescription()}</IbInfo>
      )}
      <div className={`${PASSWORD_CLASS}__form`}>
        <div className={`${PASSWORD_CLASS}__input-label`}>{t('Password')}</div>
        <IbInput
          disabled={loading || !!successMessage}
          password={!showPassword}
          status={errorMessage ? 'error' : 'default'}
          suffix={<IbButton icon={renderTogglePasswordIcon(showPassword)} type="icon" onClick={onShowPasswordClick} />}
          value={password}
          onChange={onPasswordChange}
        />
        {errorMessage && (
          <IbTypography.Paragraph error type="descriptor">
            {errorMessage}
          </IbTypography.Paragraph>
        )}
        <div className={`${PASSWORD_CLASS}__input-label`}>{t('Confirm password')}</div>
        <IbInput
          disabled={loading || !!successMessage}
          password={!showConfirmPassword}
          status={errorMessage && password !== confirmPassword ? 'error' : 'default'}
          suffix={
            <IbButton
              icon={renderTogglePasswordIcon(showConfirmPassword)}
              type="icon"
              onClick={onShowConfirmPasswordClick}
            />
          }
          value={confirmPassword}
          onChange={onConfirmPasswordChange}
        />
      </div>
      <IbButton disabled={(!password && !confirmPassword) || !!successMessage} onClick={handleSubmit}>
        {t('Save and continue')}
      </IbButton>
    </div>
  );

  return (
    <div className={MAIN_CLASS}>
      {activated ? renderSetPasswordForm() : errorMessage ? renderError() : renderDefault()}
    </div>
  );
};

export default ConfirmTrialPage;
